import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Maybe } from '@codegrade/maybe';
import client from '@/client';
import { PromiseButton } from '@/components/Button/PromiseButton';
import { Card } from '@/components/Card';
import CourseSelect from '@/components/CourseSelect';
import * as models from '@/models';
export default tsx.componentFactoryOf().create({
    name: 'snippet-import',
    props: {
        /** Current course to copy snippets into. */
        course: p(models.Course).required,
    },
    /** @returns The data for the SnippetImport. */
    data() {
        return {
            sourceCourse: Maybe.empty(),
        };
    },
    computed: {
        /** @returns Either the disabled tooltip or false for the import button. */
        importButtonDisabld() {
            return this.sourceCourse.caseOf({
                Nothing: () => ({
                    type: 'tooltip',
                    reason: 'No course selected to copy snippets from',
                }),
                Just: () => false,
            });
        },
        /** @returns The import button node. */
        importButtonNode() {
            const fromCourseName = this.sourceCourse.mapOrDefault(c => c.name, '');
            return (<div class="flex justify-end">
                    <PromiseButton data-cy="snippets-copy-btn" action={() => this.importSnippets()} prominence={4} class="min-h-9" disabled={this.importButtonDisabld} label="Import" loadingAlertMessage={`Importing snippets from ${fromCourseName}`} successAlertMessage={`Snippets from ${fromCourseName} were successfully imported`} confirm={{
                    type: 'dialog',
                    message: 'Incoming snippets with the same name as those in this course will be overwritten.',
                    popoverType: 'manual',
                }}/>
                </div>);
        },
        /** @returns The course search node. */
        courseSearchNode() {
            return (<div>
                    <div class="grow">
                        <CourseSelect class="mb-3" omit={this.course} selected={this.sourceCourse} onChange={maybeCourse => {
                    this.sourceCourse = maybeCourse;
                }}/>
                    </div>

                    <small class="text-muted">
                        Imports all snippets from the selected course into this course. Existing
                        snippets in this course will not be removed, but{' '}
                        <span class="text-pretty text-critical-60 dark:text-critical-30">
                            snippets with the same name will be overwritten.
                        </span>
                    </small>
                </div>);
        },
    },
    methods: {
        /**
         * @returns A promise after importing (copying) snippets from one course
         * to another.
         */
        async importSnippets() {
            const course = this.sourceCourse.extract();
            // Button should be disabled, so this shouldn't ever happen.
            if (course === undefined) {
                throw new Error('No source course selected');
            }
            const resp = await client.p.course.importSnippetsInto({
                intoCourseId: this.course.id,
                requestBody: {
                    fromCourseId: course.id,
                },
            });
            return resp.addCallbacks({
                onAfterSuccess: () => {
                    this.sourceCourse = Maybe.empty();
                    tsx.emitOn(this, 'onImportComplete');
                },
            });
        },
    },
    /** @returns The Snippet Import node. */
    render() {
        return (<Card data-cy="snippets-import" header="Import Snippets">
                {this.courseSearchNode}
                {this.importButtonNode}
            </Card>);
    },
});
